import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Role } from '../../config/abilityContext';
import { AcademicYear } from 'types/AcademicYearTypes';
import { PermissionPair } from 'types/PermissionTypes';
import { Instance } from 'types/superAdmin/instanceTypes';

interface User {
    id: number;
    email: string;
    name: string; // User full name
    role: Role; // Add role to the auth state
    roleId: string;
    academicYear?: AcademicYear;
    hasStaff?: boolean;
    instanceId?: string;
}


interface AuthState {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    roles: Role[];
    isSuperAdmin: boolean;
    permissions: PermissionPair[];
    workSpaces: Instance[];
}

const initialState: AuthState = {
    user: null,
    accessToken: null,
    refreshToken: null,
    roles: [],
    isSuperAdmin: false,
    permissions: [],
    workSpaces: [],
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        login: (
            state,
            action: PayloadAction<{ user: User; accessToken: string; refreshToken: string; roles: Role[]; isSuperAdmin: boolean; permissions: PermissionPair[]; workSpaces: Instance[] }>
        ) => {
            const { user, accessToken, refreshToken, roles, isSuperAdmin, permissions, workSpaces } = action.payload;
            state.user = user;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
            state.roles = roles;
            state.isSuperAdmin = isSuperAdmin;
            state.permissions = permissions;
            state.workSpaces = workSpaces;
        },
        logout: (state) => {
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.roles = [];
            state.isSuperAdmin = false;
            state.permissions = [];
            state.workSpaces = [];
        },
        updateAccessToken: (state, action: PayloadAction<string>) => {
            state.accessToken = action.payload;
        },
        updateRefreshToken: (state, action: PayloadAction<string>) => {
            state.refreshToken = action.payload;
        },
        updateAcademicYear: (state, action: PayloadAction<AcademicYear>) => {
            if (state.user) {
                state.user.academicYear = action.payload;
            }
        },
        switchWorkspace: (state, action) => {
            const { user, accessToken, refreshToken, roles, isSuperAdmin, permissions, workSpaces } = action.payload;
            state.user = user;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
            state.roles = roles;
            state.isSuperAdmin = isSuperAdmin;
            state.permissions = permissions;
            state.workSpaces = workSpaces;
        },
        updateWorkspaceLogo: (state, action: PayloadAction<{ newLogo: string | null  }>) => {
            const { newLogo } = action.payload;
            state.workSpaces = state.workSpaces.map((workspace) =>
                workspace.id === state.user?.instanceId ? { ...workspace, logo: newLogo } : workspace
            );
        }
    },
});

export const { login, logout, updateAccessToken, updateRefreshToken, updateAcademicYear, switchWorkspace, updateWorkspaceLogo } = authSlice.actions;
export default authSlice.reducer;
